import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Swal from "sweetalert2";

const Trackorder = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showsizeModal, setShowsizeModal] = useState(false);
  const [trackingDetails, setTrackingDetails] = useState(null);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchLoading, setSearchLoading] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0); // Scroll to the top when the component mounts

    // Check for orderNo in URL parameters
    const queryParams = new URLSearchParams(window.location.search);
    const orderNoFromUrl = queryParams.get("orderNo");

    if (orderNoFromUrl) {
      setSearchTerm(orderNoFromUrl);
      // Trigger the search immediately
      searchOrderByNumber(orderNoFromUrl);
    } else {
      // Only load all orders if there's no orderNo in URL
      loadOrders();
    }
  }, []); // Empty dependency array to run only once on mount

  const searchOrderByNumber = async (orderNo) => {
    try {
      setSearchLoading(true);
      setLoading(true);

      // Remove any URL encoding if present and trim whitespace
      const cleanedOrderNo = decodeURIComponent(orderNo).trim();

      // Don't proceed if order number is empty
      if (!cleanedOrderNo) {
        setOrders([]);
        return;
      }

      const response = await fetch(
        `https://vishushethra.com/api/api/orders/${cleanedOrderNo}/`,
      );

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const data = await response.json();

      // Transform the API response to match your existing orders format
      const transformedOrder = {
        order_id: data.id,
        order_no: data.order_no,
        total_price: data.total_price,
        status: data.status,
        created_at:
          data.tracking_info?.[0]?.created_at || new Date().toISOString(),
        items: [], // You might need to adjust this based on actual API response
        address: null, // You might need to adjust this based on actual API response
      };

      setOrders([transformedOrder]);
    } catch (error) {
      console.error("Error searching order:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Order not found. Please check the order number and try again.",
      });
      setOrders([]);
    } finally {
      setSearchLoading(false);
      setLoading(false);
    }
  };

  // Function to fetch orders for logged-in users
  const fetchOrderByUserId = async (userId) => {
    try {
      const response = await fetch(
        `https://vishushethra.com/api/api/fetch-order/${userId}/`,
      );

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Error in fetchOrderByUserId:", error);
      throw error;
    }
  };

  // Function to fetch orders by session for guests
  const getSessionInfo = async () => {
    try {
      const response = await fetch(
        `https://vishushethra.com/api/api/get-session-info/`,
        {
          credentials: "include",
        },
      );

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const data = await response.json();
      console.log("Session info:", data);
      return data.session_id;
    } catch (error) {
      console.error("Error fetching session info:", error);
      return null;
    }
  };

  // Function to fetch orders by session for guests
  const fetchOrdersBySession = async () => {
    try {
      // First try to get the session ID from the backend
      const sessionId = await getSessionInfo();
      let url;

      if (sessionId) {
        // If we got a session ID from the backend, use it as a query parameter
        url = `https://vishushethra.com/api/api/fetch-orders-by-session/?session_id=${sessionId}`;
      } else {
        // If not, rely on the backend's ability to get it from the request
        url = `https://vishushethra.com/api/api/fetch-orders-by-session/`;
      }

      console.log("Fetching orders with URL:", url);

      const response = await fetch(url, {
        credentials: "include", // This ensures cookies are sent with the request
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.error || `HTTP error! Status: ${response.status}`,
        );
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Error in fetchOrdersBySession:", error);
      throw error;
    }
  };

  const loadOrders = async () => {
    try {
      setLoading(true);

      // If there's a search term, use the search function
      if (searchTerm.trim()) {
        await searchOrderByNumber(searchTerm.trim());
        return;
      }

      // Original loading logic for non-search cases
      let data;
      const userId = localStorage.getItem("userId");
      const isLoggedIn = !!userId;

      if (isLoggedIn) {
        data = await fetchOrderByUserId(userId);
      } else {
        data = await fetchOrdersBySession();
      }

      if (data && Array.isArray(data.orders)) {
        const sortedOrders = data.orders.sort(
          (a, b) => new Date(b.created_at) - new Date(a.created_at),
        );
        setOrders(sortedOrders);
      } else {
        setOrders([]);
      }
    } catch (error) {
      console.error("Failed to load orders", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to load orders. Please try again.",
      });
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    loadOrders();
  };

  // Add this to reset the search and load all orders
  const handleResetSearch = () => {
    setSearchTerm("");
    loadOrders();
  };

  const handleTrackOrder = async (orderId) => {
    try {
      const response = await fetch(
        `https://vishushethra.com/api/api/order_track/${orderId}/`,
      );

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const data = await response.json();

      // Transform the API response to match your existing trackingDetails format
      const trackingDetails = {
        order: data.order,
        tracking_number: data.tracking_number,
        tracking_url: data.tracking_url,
        status: data.is_delivered
          ? "delivered"
          : data.is_shipped
            ? "shipped"
            : "processing",
        shipped_date: data.shipped_date,
        estimated_delivery: null, // Not provided in new API
        current_location: null, // Not provided in new API
        // Create tracking steps based on the status
        tracking_steps: [
          {
            event: "Order Placed",
            completed: true,
            timestamp: data.created_at,
          },
          {
            event: "Shipped",
            completed: data.is_shipped,
            timestamp: data.shipped_date,
          },
          {
            event: "Delivered",
            completed: data.is_delivered,
            timestamp: data.delivered_date,
          },
        ],
      };

      setTrackingDetails(trackingDetails);
      setShowsizeModal(true);
    } catch (error) {
      console.error("Failed to load tracking details", error);
      Swal.fire({
        icon: "error",
        title: "Sorry!",
        text: "Kindly check back later, tracking details are not available right now.",
      });
    }
  };

  const [copySuccess, setCopySuccess] = useState(false);

  const handleCopy = (e) => {
    e.preventDefault();
    if (!trackingDetails?.tracking_number) return;

    navigator.clipboard
      .writeText(trackingDetails.tracking_number)
      .then(() => {
        setCopySuccess(true);
        setTimeout(() => {
          setCopySuccess(false);
          if (trackingDetails.tracking_url) {
            window.open(trackingDetails.tracking_url, "_blank");
          }
        }, 1000);
      })
      .catch((err) => {
        console.error("Error copying text: ", err);
        setCopySuccess(false);
      });
  };

  const togglesizeModal = () => setShowsizeModal(!showsizeModal);

  const formatDate = (dateString) => {
    if (!dateString) return "Pending";
    const date = new Date(dateString);
    return date.toLocaleString(); // You can customize this format as needed
  };

  const formatStatus = (status) => {
    if (!status) return "";
    return status
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <div>
      <div className="banner-section  ">
        <img
          src="./assets/img/bannerbreadcrumbs.jpg"
          alt="Banner"
          className="img-fluid banner-img"
        />

        <div className="breadcrumb-container ">
          <h2>Track Order</h2>
          <nav aria-label="breadcrumb">
            <ol className="breadcrumb mb-0">
              <li className="breadcrumb-item">
                <Link to="/">Home</Link>
              </li>

              <li className="breadcrumb-item active" aria-current="page">
                Track Order
              </li>
            </ol>
          </nav>
        </div>
      </div>

      <div className="shopping_cart_area mt-5">
        <div className="container">
          <div className="row mb-4">
            <div className="col-md-8 offset-md-2">
              <form onSubmit={handleSearchSubmit} className="d-flex">
                <div className="input-group">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Enter Order Number (e.g., 43XDFGG8)"
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                      // Optional: trigger search on change after debounce
                      // You might want to add debouncing here if you want live search
                    }}
                  />
                  <button
                    className="trackpage-searchbtn"
                    type="submit"
                    disabled={searchLoading}
                  >
                    {searchLoading ? "Searching..." : "Search"}
                  </button>
                  {searchTerm && (
                    <button
                      className="btn btn-outline-secondary"
                      type="button"
                      onClick={handleResetSearch}
                    >
                      Clear
                    </button>
                  )}
                </div>
              </form>
            </div>
          </div>
          <form action="#">
            <div className="cart_page_inner mb-60">
              <div className="row">
                <div className="col-12">
                  <div className="cart_page_tabel">
                    <table>
                      <thead>
                        <tr>
                          <th>Product</th>
                          <th className="text-center">Information</th>
                          <th>Product Detail</th>
                          <th>Price</th>
                          <th>Track</th>
                        </tr>
                      </thead>
                      <tbody>
                        {orders.map((order) =>
                          order.items && order.items.length > 0 ? (
                            order.items.map((item) => (
                              <tr key={item.product_id} className="border-top">
                                <td>
                                  <div className="cart_product_thumb">
                                    <img
                                      src={
                                        item.images && item.images[0]
                                          ? item.images[0]
                                          : "placeholder.jpg"
                                      }
                                      alt={item.product_name}
                                    />
                                  </div>
                                </td>
                                <td>
                                  <div className="cart_product_text">
                                    <h4>{item.product_name}</h4>
                                    <ul>
                                      <li>
                                        Quantity: <span>{item.quantity}</span>
                                      </li>
                                      <li>
                                        Size:{" "}
                                        <span>
                                          {item.selected_size || "N/A"}
                                        </span>
                                      </li>
                                    </ul>
                                  </div>
                                </td>
                                <td>
                                  <div className="cart_product_remove text-center">
                                    <Link
                                      to={`/Productdetail/${item.product_id}`}
                                    >
                                      <button
                                        className="btn btn-primary"
                                        type="button"
                                      >
                                        View
                                      </button>
                                    </Link>
                                  </div>
                                </td>
                                <td>
                                  <div className="cart_product_price">
                                    <span>
                                      ₹
                                      {parseFloat(
                                        item.total_price,
                                      ).toLocaleString("en-IN", {
                                        minimumFractionDigits: 2,
                                      })}
                                    </span>
                                  </div>
                                </td>
                                <td>
                                  <div className="cart_product_remove text-center">
                                    <button
                                      className="btn btn-danger"
                                      type="button"
                                      onClick={() =>
                                        handleTrackOrder(order.order_id)
                                      }
                                    >
                                      Track your order
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr key={order.order_id} className="border-top">
                              <td colSpan="5" className="text-center">
                                <div>
                                  <p>Order ID: {order.order_id}</p>
                                  <p>Order Amount: ₹{order.total_price}</p>
                                  {order.address && (
                                    <p>
                                      Address: {order.address.street},{" "}
                                      {order.address.city},{" "}
                                      {order.address.state},{" "}
                                      {order.address.postal_code},{" "}
                                      {order.address.country}
                                    </p>
                                  )}
                                  <button
                                    className="btn btn-danger"
                                    type="button"
                                    onClick={() =>
                                      handleTrackOrder(order.order_id)
                                    }
                                  >
                                    Track your order
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ),
                        )}
                      </tbody>
                    </table>
                    <hr />
                  </div>
                  {/* Mobile view content */}
                  <div className="cart_page_mobiletabel">
                    {loading ? (
                      <p>Loading...</p>
                    ) : error ? (
                      <p>{error}</p>
                    ) : (
                      <div>
                        {orders.length > 0 ? (
                          orders.map((order) =>
                            order.items && order.items.length > 0 ? (
                              order.items.map((item) => (
                                <div
                                  key={item.product_id}
                                  className="cart_item_mobile"
                                >
                                  <div className="cart_item_mobile_info">
                                    <div className="cart_product_thumb">
                                      <img
                                        src={
                                          item.images && item.images[0]
                                            ? item.images[0]
                                            : "placeholder.jpg"
                                        }
                                        alt={item.product_name}
                                      />
                                    </div>
                                    <div>
                                      <div className="cart_item_mobile_price">
                                        <span>
                                          ₹
                                          {parseFloat(
                                            item.total_price,
                                          ).toLocaleString("en-IN", {
                                            minimumFractionDigits: 2,
                                          })}{" "}
                                          <span style={{ fontSize: "13px" }}>
                                            INR
                                          </span>
                                        </span>
                                      </div>
                                      <h4>{item.product_name}</h4>
                                      <ul style={{ paddingLeft: "0px" }}>
                                        <li>
                                          Quantity: <span>{item.quantity}</span>
                                        </li>
                                        <li>
                                          Size:{" "}
                                          <span>
                                            {item.selected_size || "N/A"}
                                          </span>
                                        </li>
                                      </ul>
                                    </div>
                                  </div>

                                  <div className="cart_item_mobile_actions">
                                    <div className="cart_item_mobile_">
                                      <Link
                                        to={`/Productdetail/${item.product_id}`}
                                      >
                                        <button
                                          className="btn btn-primary"
                                          type="button"
                                        >
                                          View
                                        </button>
                                      </Link>
                                    </div>
                                    <div>
                                      <button
                                        className="btn btn-danger"
                                        type="button"
                                        onClick={() =>
                                          handleTrackOrder(order.order_id)
                                        }
                                      >
                                        Track your order
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              ))
                            ) : (
                              <div
                                key={order.order_id}
                                className="cart_item_mobile"
                              >
                                <p>Order ID: {order.order_id}</p>
                                <p>Order Amount: ₹{order.total_price}</p>
                                {order.address && (
                                  <p>
                                    Address: {order.address.street},{" "}
                                    {order.address.city}, {order.address.state},{" "}
                                    {order.address.postal_code},{" "}
                                    {order.address.country}
                                  </p>
                                )}
                                <button
                                  className="btn btn-danger"
                                  type="button"
                                  onClick={() =>
                                    handleTrackOrder(order.order_id)
                                  }
                                >
                                  Track your order
                                </button>
                              </div>
                            ),
                          )
                        ) : (
                          <div>No orders found</div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>

      {/* Modal for tracking details */}
      {showsizeModal && trackingDetails && (
        <div className="modal-overlay-size" onClick={togglesizeModal}>
          <div
            className="modal-content-size"
            onClick={(e) => e.stopPropagation()}
          >
            <span className="close-button-size" onClick={togglesizeModal}>
              &times;
            </span>

            <div>
              <h2 style={{ textAlign: "center" }}>Track Your Order</h2>

              <div className="tracking-info">
                <p>
                  <strong>Tracking Number:</strong>{" "}
                  {trackingDetails.tracking_number || "Not available"}
                  {trackingDetails.tracking_number && (
                    <button
                      onClick={handleCopy}
                      style={{
                        marginLeft: "10px",
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        color: copySuccess ? "green" : "#007bff",
                      }}
                      title="Copy tracking number"
                    >
                      {copySuccess ? "✓ Copied!" : "⎘ Copy"}
                    </button>
                  )}
                </p>

                <p>
                  <strong>Order ID:</strong>{" "}
                  {trackingDetails.order || "Not available"}
                </p>

                <p>
                  <strong>Status:</strong>{" "}
                  {formatStatus(trackingDetails.status) || "Processing"}
                </p>

                <p>
                  <strong>Shipped Date:</strong>{" "}
                  {trackingDetails.shipped_date
                    ? formatDate(trackingDetails.shipped_date)
                    : "Not shipped yet"}
                </p>

                <p>
                  <strong>Estimated Delivery:</strong>{" "}
                  {trackingDetails.estimated_delivery
                    ? formatDate(trackingDetails.estimated_delivery)
                    : "Not available"}
                </p>

                <p>
                  <strong>Current Location:</strong>{" "}
                  {trackingDetails.current_location || "Not available"}
                </p>

                {trackingDetails.tracking_url && (
                  <p>
                    <strong>Tracking URL:</strong>{" "}
                    <a
                      href={trackingDetails.tracking_url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Track Package
                    </a>
                  </p>
                )}
              </div>

              <div className="trackbox">
                <hr className="tracking-line" />
                <ul className="progressList">
                  {trackingDetails.tracking_steps?.map((step, index) => (
                    <li
                      key={index}
                      className={`progressList_item ${
                        !step.completed ? "pending" : ""
                      }`}
                    >
                      <div
                        className={`progressList_content ${
                          step.completed
                            ? step.event === "Delivered"
                              ? "green"
                              : step.event === "Out for delivery"
                                ? "orange"
                                : "green"
                            : "gray"
                        }`}
                      >
                        {step.event}
                        {step.timestamp && (
                          <small>{formatDate(step.timestamp)}</small>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        .modal-overlay-size {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: rgba(0, 0, 0, 0.6);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
        }

        .modal-content-size {
          background: white;
          padding: 20px;
          border-radius: 10px;
          max-width: 600px;
          width: 100%;
          position: relative;
          box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
          max-height: 80vh;
          overflow-y: auto;
        }

        .close-button-size {
          position: absolute;
          top: 10px;
          right: 10px;
          font-size: 24px;
          cursor: pointer;
        }

        .tracking-info p {
          font-size: 16px;
          margin: 10px 0;
          color: #555;
        }
        .trackbox {
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: center;
        }

        .tracking-line {
          border: none;
          height: 1px;
          background: #ddd;
          margin: 20px 0;
          width: 100%;
        }

        .progressList {
          list-style-type: none;
          padding: 0;
          margin: 0;
          position: relative;
          width: 100%;
        }

        .progressList_item {
          display: flex;
          align-items: center;
          padding: 15px 0;
          position: relative;
        }

        /* Default Gray Color */
        .progressList_item .progressList_content {
          position: relative;
          font-size: 14px;
          padding-left: 25px;
          display: inline-block;
          white-space: nowrap;
          vertical-align: middle;
          width: 100%;
        }

        .progressList_item .progressList_content::before {
          content: "";
          position: absolute;
          left: 0;
          top: 50%;
          width: 12px;
          height: 12px;
          border-radius: 50%;
          background-color: #9e9e9e;
          transform: translateY(-50%);
        }

        /* Green for completed steps */
        .progressList_item .progressList_content.green {
          color: #4caf50;
        }

        .progressList_item .progressList_content.green::before {
          background-color: #4caf50;
        }

        /* Orange for in-progress steps */
        .progressList_item .progressList_content.orange {
          color: #ff9800;
        }

        .progressList_item .progressList_content.orange::before {
          background-color: #ff9800;
        }

        /* Gray for pending steps */
        .progressList_item .progressList_content.gray {
          color: #9e9e9e;
        }

        .progressList_item .progressList_content.gray::before {
          background-color: #9e9e9e;
        }

        .progressList_item .progressList_content small {
          display: block;
          font-size: 12px;
          color: #666;
          margin-top: 4px;
          white-space: normal;
        }

        @media (max-width: 600px) {
          .modal-content-size {
            padding: 15px;
            width: 90%;
          }

          .close-button-size {
            top: 8px;
            right: 8px;
            font-size: 22px;
          }

          .tracking-info p {
            font-size: 14px;
          }

          .progressList_content {
            font-size: 14px;
          }
        }
      `}</style>
    </div>
  );
};

// Export this function to use in your email template
export function generateTrackOrderLink(orderNo) {
  // Encode the order number to handle special characters
  const encodedOrderNo = encodeURIComponent(orderNo);
  // Create the full URL with the order number as a query parameter
  return `https://ecom.vishushethra.com/Trackorder?orderNo=${encodedOrderNo}`;
}

export default Trackorder;
